import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

export interface SelectedProfile {
  id: string;
  type: "candidats" | "enseignes" | "villes" | "locations";
  entity: any;
  // FROZEN filters at the time of selection
  frozenFilters: Array<{ id: string; type: string }>;
}

interface MainProfile {
  id: number;
  type: "candidat" | "enseigne" | "ville" | "location";
}

interface ProfileDrawerState {
  profileId: string;
  profileType: string;
}

interface CampaignContextType {
  selectedProfiles: SelectedProfile[];
  selectProfile: (profile: Omit<SelectedProfile, "frozenFilters">) => void;
  deselectProfile: (id: string, type: string) => void;
  isProfileSelected: (id: string, type: string) => boolean;
  getSelectedForType: (type: string) => SelectedProfile | undefined;
  clearSelection: () => void;
  hasMinimumSelection: boolean;
  // NEW: Centralized filter management
  getFiltersForBlock: (
    blockType: string,
  ) => Array<{ id: string; type: string }>;
  hasSelection: (type: string) => boolean;
  // Profile drawer
  selectedEntity: any | null;
  setSelectedEntity: (entity: any | null) => void;
  // Main profile for tooltip comparison
  mainProfile: MainProfile;
}

const CampaignContext = createContext<CampaignContextType | undefined>(
  undefined,
);

interface CampaignProviderProps {
  children: ReactNode;
  mainProfile: MainProfile;
}

export function CampaignProvider({
  children,
  mainProfile,
}: CampaignProviderProps) {
  const [selectedProfiles, setSelectedProfiles] = useState<SelectedProfile[]>(
    [],
  );
  const [selectedEntity, setSelectedEntity] = useState<any | null>(null);

  // CORE LOGIC: Compute filters for a specific block
  // This is the SINGLE SOURCE OF TRUTH for what filters each block should receive
  const getFiltersForBlock = useCallback(
    (blockType: string): Array<{ id: string; type: string }> => {
      // CHECK: If this block already has a selection, return its FROZEN filters
      const existingSelection = selectedProfiles.find(
        (p) => p.type === blockType,
      );
      if (existingSelection) {
        return existingSelection.frozenFilters;
      }

      // Otherwise, compute fresh filters
      const filters: Array<{ id: string; type: string }> = [];

      // RULE 1: Always include main profile as filter
      const mainProfileType = `${mainProfile.type}s` as
        | "candidats"
        | "enseignes"
        | "villes"
        | "locations";
      if (mainProfile.id != null) {
        filters.push({
          id: mainProfile.id.toString(),
          type: mainProfileType,
        });
      }

      // RULE 2: Include selected profiles EXCEPT if they match the target block type
      // (because selected blocks should NOT be passed as filter parameters)
      selectedProfiles.forEach((profile) => {
        if (profile.type !== blockType) {
          filters.push({
            id: profile.id,
            type: profile.type,
          });
        }
      });

      return filters;
    },
    [mainProfile, selectedProfiles],
  );

  const selectProfile = (profile: Omit<SelectedProfile, "frozenFilters">) => {
    setSelectedProfiles((prev) => {
      // Compute and freeze the current filters for this block type
      const frozenFilters = getFiltersForBlock(profile.type);

      // Replace any existing selection for this type
      const filtered = prev.filter((p) => p.type !== profile.type);
      return [...filtered, { ...profile, frozenFilters }];
    });
  };

  const deselectProfile = (id: string, type: string) => {
    setSelectedProfiles((prev) =>
      prev.filter((p) => !(p.id === id && p.type === type)),
    );
  };

  const isProfileSelected = (id: string, type: string) => {
    return selectedProfiles.some((p) => p.id === id && p.type === type);
  };

  const getSelectedForType = (type: string) => {
    return selectedProfiles.find((p) => p.type === type);
  };

  const hasSelection = useCallback(
    (type: string) => {
      return selectedProfiles.some((p) => p.type === type);
    },
    [selectedProfiles],
  );

  const clearSelection = () => {
    setSelectedProfiles([]);
  };

  const hasMinimumSelection = selectedProfiles.length >= 2;

  return (
    <CampaignContext.Provider
      value={{
        selectedProfiles,
        selectProfile,
        deselectProfile,
        isProfileSelected,
        getSelectedForType,
        clearSelection,
        hasMinimumSelection,
        getFiltersForBlock,
        hasSelection,
        selectedEntity,
        setSelectedEntity,
        mainProfile,
      }}
    >
      {children}
    </CampaignContext.Provider>
  );
}

export function useCampaign() {
  const context = useContext(CampaignContext);
  if (context === undefined) {
    throw new Error("useCampaign must be used within a CampaignProvider");
  }
  return context;
}
